import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { useTwoFaDisable, useTwoFaEnable, useTwoFaSetup } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { NOUN, pluralize } from '@/lib/plural';
import type { TwoFaSetup } from '@/types/api';

const CODE_LENGTH = 6;

/**
 * Two-factor enrolment. The QR is rendered locally from the otpauth URI, so the
 * shared secret never travels to a third-party image service.
 */
export function TwoFactorSection({ enabled }: { enabled: boolean }) {
  const toast = useToast();
  const setup = useTwoFaSetup();
  const enable = useTwoFaEnable();
  const disable = useTwoFaDisable();

  const [enrolment, setEnrolment] = useState<TwoFaSetup | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draw the QR whenever a fresh enrolment arrives. The encoder is imported
  // dynamically so ~30 kB of QR code never ships to a trader who only ever
  // opens Traffic.
  useEffect(() => {
    if (!enrolment) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;

    void (async () => {
      try {
        const { toDataURL } = await import('qrcode');
        const url = await toDataURL(enrolment.otpauthUrl, {
          margin: 1,
          width: 220,
          errorCorrectionLevel: 'M',
        });
        if (!cancelled) setQrDataUrl(url);
      } catch {
        if (!cancelled) {
          setError('Не удалось нарисовать QR-код — введите секрет вручную.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enrolment]);

  const startEnrolment = async (): Promise<void> => {
    setError(null);
    setCode('');
    try {
      setEnrolment(await setup.mutateAsync());
    } catch (setupError) {
      setError(toMessage(setupError, 'Не удалось начать настройку двухфакторной аутентификации.'));
    }
  };

  const confirmEnrolment = async (): Promise<void> => {
    if (code.trim().length !== CODE_LENGTH) {
      setError(
        `Введите ${pluralize(CODE_LENGTH, NOUN.digit)} из приложения-аутентификатора.`,
      );
      return;
    }
    setError(null);
    try {
      await enable.mutateAsync({ code: code.trim() });
      setEnrolment(null);
      setCode('');
      toast.success('Двухфакторная аутентификация включена');
    } catch (enableError) {
      setError(toMessage(enableError, 'Код не принят.'));
    }
  };

  const confirmDisable = async (): Promise<void> => {
    if (password.length === 0) {
      setError('Введите пароль, чтобы отключить двухфакторную аутентификацию.');
      return;
    }
    setError(null);
    try {
      await disable.mutateAsync({ password });
      setIsDisabling(false);
      setPassword('');
      toast.info('Двухфакторная аутентификация отключена', 'Теперь аккаунт защищён слабее.');
    } catch (disableError) {
      setError(toMessage(disableError, 'Не удалось отключить двухфакторную аутентификацию.'));
    }
  };

  return (
    <Panel padding="none">
      <PanelHeader
        title="Двухфакторная аутентификация"
        description="Код из приложения-аутентификатора при каждом входе."
        actions={enabled ? <Badge tone="ok">Включена</Badge> : <Badge tone="warn">Отключена</Badge>}
      />

      <div className="px-4 py-4">
        {error && !enrolment && !isDisabling ? (
          <InlineError message={error} className="mb-4" />
        ) : null}

        <div className="flex flex-wrap items-start justify-between gap-4">
          <p className="max-w-prose text-sm leading-normal text-fg-secondary">
            {enabled
              ? 'При каждом входе аккаунт запрашивает код из приложения-аутентификатора. Храните резервные коды не на этом компьютере.'
              : 'Без второго фактора любой, у кого есть ваш пароль, сможет подтверждать платежи от вашего имени. Используйте приложение-аутентификатор — например Aegis, 1Password или Google Authenticator.'}
          </p>

          {enabled ? (
            <Button variant="danger" onClick={() => setIsDisabling(true)}>
              Отключить
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={<ShieldCheck size={14} strokeWidth={1.5} aria-hidden="true" />}
              isLoading={setup.isPending}
              onClick={() => void startEnrolment()}
            >
              {setup.isPending ? 'Готовлю…' : 'Настроить'}
            </Button>
          )}
        </div>
      </div>

      {/* Enrolment */}
      <Dialog
        open={enrolment !== null}
        onClose={() => {
          setEnrolment(null);
          setError(null);
        }}
        busy={enable.isPending}
        title="Настройка двухфакторной аутентификации"
        description="Отсканируйте код приложением-аутентификатором и введите шесть цифр, которые оно покажет."
        footer={
          <>
            <Button onClick={() => setEnrolment(null)} disabled={enable.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              isLoading={enable.isPending}
              onClick={() => void confirmEnrolment()}
            >
              {enable.isPending ? 'Проверяю…' : 'Проверить и включить'}
            </Button>
          </>
        }
      >
        {error ? <InlineError message={error} className="mb-4" /> : null}

        <div className="flex flex-col gap-5 sm:flex-row">
          {/* Square and fluid up to 220px: a hard 220px box plus the sheet's
              own padding overflows a 320px viewport. */}
          <div className="flex aspect-square w-full max-w-[13.75rem] shrink-0 items-center justify-center rounded-md border border-line bg-n-0 p-2">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="QR-код для подключения двухфакторной аутентификации"
                className="h-full w-full object-contain"
              />
            ) : (
              <p className="text-xs text-fg-muted">Рисую код…</p>
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <p className="eyebrow">Или введите секрет вручную</p>
              <p className="num mt-1 break-all rounded-sm border border-line bg-sunken px-2 py-1.5 text-sm text-fg">
                {enrolment?.secret}
              </p>
            </div>

            <TextField
              label="Код из шести цифр"
              monospace
              inputMode="numeric"
              // Older iOS looks at `pattern`, not `inputMode`, before deciding
              // to show the numeric pad.
              pattern="[0-9]*"
              enterKeyHint="done"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              placeholder="000000"
              value={code}
              disabled={enable.isPending}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>
      </Dialog>

      {/* Disable */}
      <Dialog
        open={isDisabling}
        onClose={() => {
          setIsDisabling(false);
          setError(null);
        }}
        busy={disable.isPending}
        size="sm"
        title="Отключить двухфакторную аутентификацию?"
        description="Аккаунт останется защищён только паролем."
        footer={
          <>
            <Button onClick={() => setIsDisabling(false)} disabled={disable.isPending}>
              Оставить включённой
            </Button>
            <Button
              variant="danger"
              isLoading={disable.isPending}
              onClick={() => void confirmDisable()}
            >
              {disable.isPending ? 'Отключаю…' : 'Отключить'}
            </Button>
          </>
        }
      >
        {error ? <InlineError message={error} className="mb-4" /> : null}
        <TextField
          label="Ваш пароль"
          type="password"
          enterKeyHint="done"
          autoComplete="current-password"
          value={password}
          disabled={disable.isPending}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Dialog>
    </Panel>
  );
}
